import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QuickCompatibilityForm from '../../components/synastry/quick/QuickCompatibilityForm';
import QuickResultCard from '../../components/synastry/quick/QuickResultCard';
import useSynastryQuick from '../../games/hooks/useSynastryQuick';
import { translateSign } from '../../utils/zodiacTranslations';
import { saveQuickSynastry, listSavedSynastries, getSavedSynastryById } from '../../services/savedSynastryApi';
import { clearLegacySavedSynastryStorage } from '../../utils/synastryPrivateStorage';
import { localizedValue } from '../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

const QUICK_STARS_STYLE_ID = 'quick-stars-style';

function readSavedSynastriesFromStorage() {
  clearLegacySavedSynastryStorage();
  return [];
}

function getSavedQuickResultPayload(item) {
  return (
    item?.resultSnapshot ||
    item?.result ||
    item?.reading ||
    item?.raw?.resultSnapshot ||
    item?.raw?.result ||
    item?.raw?.reading ||
    null
  );
}

function inferSavedQuickMode(item) {
  const mode = String(
    item?.mode ||
      item?.readingType ||
      item?.calculationMode ||
      item?.raw?.mode ||
      item?.raw?.readingType ||
      item?.raw?.calculationMode ||
      ''
  )
    .trim()
    .toLowerCase();

  if (mode === 'quick' || mode === 'deep') return mode;

  const source = String(
    item?.source ||
      item?.sourceType ||
      item?.raw?.source ||
      item?.raw?.sourceType ||
      ''
  )
    .trim()
    .toLowerCase();

  if (source.includes('quick') || source.includes('solar')) return 'quick';
  if (source.includes('deep') || source.includes('synastry')) return 'deep';

  const payload = getSavedQuickResultPayload(item);
  const dims = payload?.compatibility?.dimensions || payload?.dimensions || {};

  const looksQuick = Boolean(
    payload?.pair?.signA && payload?.pair?.signB
  ) || Boolean(
    dims &&
      typeof dims === 'object' &&
      ('energy' in dims || 'expression' in dims || 'magnetism' in dims || 'growth' in dims)
  );

  if (looksQuick) return 'quick';
  return 'unknown';
}

async function readRemoteQuickSavedSynastries() {
  try {
    const response = await listSavedSynastries(50);
    const remoteItems = Array.isArray(response?.items) ? response.items : [];

    const hydrated = await Promise.all(
      remoteItems.map(async (item) => {
        try {
          const detail = await getSavedSynastryById(item.id);
          const saved = detail?.saved || null;

          return {
            ...(saved || item),
            id: item.id,
            _id: item.id,
            title: saved?.title || item.title,
            mode: saved?.mode || item.mode,
            sourceType: saved?.sourceType || item.sourceType,
            compatibilityScore: saved?.compatibilityScore ?? item.compatibilityScore,
            personA: saved?.personA || item.personA,
            personB: saved?.personB || item.personB,
            resultSnapshot: saved?.resultSnapshot || item.resultSnapshot,
            result: saved?.resultSnapshot || item.result,
            reading: saved?.resultSnapshot || item.reading,
            tags: saved?.tags || item.tags,
            createdAt: saved?.createdAt || item.createdAt,
            updatedAt: saved?.updatedAt || item.updatedAt,
          };
        } catch {
          return {
            ...item,
            id: item.id,
            _id: item.id,
          };
        }
      })
    );

    return hydrated.filter((item) => inferSavedQuickMode(item) === 'quick');
  } catch {
    return [];
  }
}

function mergeQuickCandidateLists(...lists) {
  const merged = [];
  const seen = new Set();

  lists.flat().forEach((item) => {
    if (!item) return;

    const id = String(item?.id || item?._id || '').trim();
    const fingerprint = String(item?.fingerprint || item?.raw?.fingerprint || '').trim();
    const dedupeKey = id || (fingerprint ? `fp:${fingerprint}` : '');

    if (dedupeKey && seen.has(dedupeKey)) return;
    if (dedupeKey) seen.add(dedupeKey);

    merged.push(item);
  });

  return merged;
}



function getInitialQuickSigns(initialSavedReading) {
  const payload = getSavedQuickResultPayload(initialSavedReading) || initialSavedReading || null;

  const rawSignA = String(
    payload?.pair?.signA ||
      initialSavedReading?.personA?.sign ||
      initialSavedReading?.personA?.sunSign ||
      ''
  )
    .trim()
    .toLowerCase();

  const rawSignB = String(
    payload?.pair?.signB ||
      initialSavedReading?.personB?.sign ||
      initialSavedReading?.personB?.sunSign ||
      ''
  )
    .trim()
    .toLowerCase();

  const signA = normalizeQuickSignKey(rawSignA);
  const signB = normalizeQuickSignKey(rawSignB);

  return {
    signA: signA || 'leo',
    signB: signB || 'pisces',
  };
}

function normalizeQuickSignKey(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const map = {
    aries: 'aries',
    tauro: 'taurus',
    taurus: 'taurus',
    geminis: 'gemini',
    gemini: 'gemini',
    cancer: 'cancer',
    leo: 'leo',
    virgo: 'virgo',
    libra: 'libra',
    escorpio: 'scorpio',
    scorpio: 'scorpio',
    sagitario: 'sagittarius',
    sagittarius: 'sagittarius',
    capricornio: 'capricorn',
    capricorn: 'capricorn',
    acuario: 'aquarius',
    aquarius: 'aquarius',
    piscis: 'pisces',
    pisces: 'pisces',
  };

  return map[raw] || raw;
}

const styles = {
  wrapper: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr)',
    gap: 22,
    padding: '6px 2px 4px',
    position: 'relative',
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: '.6px',
    color: '#FFE7A3',
    textShadow: '0 0 12px rgba(218,165,32,0.45)',
    marginBottom: 4,
  },
  cosmicBg: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.6), transparent), radial-gradient(1px 1px at 70% 20%, rgba(255,255,255,0.5), transparent), radial-gradient(1px 1px at 40% 80%, rgba(255,255,255,0.5), transparent), radial-gradient(1px 1px at 85% 60%, rgba(255,255,255,0.5), transparent)',
    opacity: 0.35,
    animation: 'quickStars 10s linear infinite',
  },
  topActions: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '2px 2px 6px',
  },
  libraryButton: {
    minHeight: 54,
    border: '1px solid rgba(247,215,116,0.46)',
    borderRadius: 18,
    padding: '0 18px',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(247,215,116,0.10) 42%, rgba(138,43,226,0.12) 100%)',
    color: '#FFF3C4',
    fontWeight: 900,
    fontSize: 14,
    letterSpacing: '.35px',
    cursor: 'pointer',
    boxShadow:
      '0 18px 44px rgba(0,0,0,0.35), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    transition: 'transform .18s ease, box-shadow .18s ease, filter .18s ease',
  },
  libraryButtonHover: {
    transform: 'translateY(-2px)',
    boxShadow:
      '0 22px 52px rgba(0,0,0,0.42), 0 0 28px rgba(247,215,116,0.34), 0 0 18px rgba(138,43,226,0.18)',
    filter: 'brightness(1.03)',
  },
  introCard: {
    borderRadius: 20,
    padding: '20px 20px',
    border: '1px solid rgba(218,165,32,0.35)',
    background:
      'linear-gradient(160deg, rgba(45,10,80,0.55), rgba(10,5,30,0.85))',
    color: '#F5E8FF',
    fontSize: 14,
    lineHeight: 1.55,
    boxShadow:
      '0 12px 40px rgba(0,0,0,0.55), 0 0 16px rgba(218,165,32,0.15), inset 0 0 18px rgba(255,255,255,0.04)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
  },
  introTitle: {
    fontSize: 19,
    fontWeight: 900,
    color: '#FFE7A3',
    marginBottom: 8,
    letterSpacing: '.45px',
    textShadow: '0 0 8px rgba(218,165,32,0.45)',
  },
  error: {
    borderRadius: 16,
    padding: '14px 16px',
    border: '1px solid rgba(255,100,100,0.22)',
    background: 'rgba(255,70,70,0.10)',
    color: '#FFD5D5',
    fontWeight: 700,
  },
  resultSection: {
    position: 'relative',
    borderRadius: 22,
    padding: '18px 18px 22px',
    border: '1px solid rgba(218,165,32,0.25)',
    background:
      'linear-gradient(160deg, rgba(35,10,60,0.55), rgba(5,5,20,0.85))',
    boxShadow:
      '0 18px 50px rgba(0,0,0,0.65), 0 0 18px rgba(218,165,32,0.12), inset 0 0 22px rgba(255,255,255,0.03)',
    backdropFilter: 'blur(10px)',
    overflow: 'hidden',
  },
};

function buildQuickSavedPayload(result) {
  const signA = translateSign(result?.pair?.signA || '');
  const signB = translateSign(result?.pair?.signB || '');
  const title = [signA, signB].filter(Boolean).join(' y ') || 'Compatibilidad solar';

  const personA = {
    nickname: signA || 'Persona A',
    sign: signA || result?.pair?.signA || '',
    sunSign: signA || result?.pair?.signA || '',
    zodiacSign: signA || result?.pair?.signA || '',
  };

  const personB = {
    nickname: signB || 'Persona B',
    sign: signB || result?.pair?.signB || '',
    sunSign: signB || result?.pair?.signB || '',
    zodiacSign: signB || result?.pair?.signB || '',
  };

  return {
    title,
    personA,
    personB,
    resultSnapshot: result,
    result,
    reading: result,
    mode: 'quick',
    calculationMode: 'quick',
    readingType: 'quick',
    source: 'quick-solar',
  };
}

export default function QuickModeTab({ initialSavedReading = null, refreshCompareStateKey = '' }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    clearLegacySavedSynastryStorage();
  }, []);

  // Persist the active synastry tab so Biblioteca Cósmica knows where to return
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('synastry:mode', 'quick');
      }
    } catch {}
  }, []);

  const initialQuickSigns = useMemo(
    () => getInitialQuickSigns(initialSavedReading),
    [initialSavedReading]
  );

  const initialQuickResult = useMemo(
    () => getSavedQuickResultPayload(initialSavedReading),
    [initialSavedReading]
  );

  const [activeInitialQuickResult, setActiveInitialQuickResult] = useState(initialQuickResult || null);

  useEffect(() => {
    setActiveInitialQuickResult(initialQuickResult || null);
  }, [initialQuickResult]);

  const {
    signA,
    signB,
    setSignA,
    setSignB,
    loading,
    result,
    error,
    calculate,
  } = useSynastryQuick({
    signA: initialQuickSigns.signA,
    signB: initialQuickSigns.signB,
  });

  // When opening a saved quick reading we must sync the hook state
  // with the signs from that reading so the form and internal state match.
  useEffect(() => {
    if (!initialSavedReading) return;

    if (initialQuickSigns.signA) setSignA(initialQuickSigns.signA);
    if (initialQuickSigns.signB) setSignB(initialQuickSigns.signB);
  }, [initialSavedReading, initialQuickSigns, setSignA, setSignB]);

  useEffect(() => {
    if (!activeInitialQuickResult?.pair) return;

    const savedA = normalizeQuickSignKey(activeInitialQuickResult.pair.signA);
    const savedB = normalizeQuickSignKey(activeInitialQuickResult.pair.signB);
    const currentA = normalizeQuickSignKey(signA);
    const currentB = normalizeQuickSignKey(signB);

    if (savedA !== currentA || savedB !== currentB) {
      setActiveInitialQuickResult(null);
    }
  }, [activeInitialQuickResult, signA, signB]);

  // NOTE:
  // We intentionally do NOT override the form signs with the saved reading signs.
  // The result card can display the saved compatibility, but the form should stay
  // free so the user can immediately calculate a new compatibility.
  // Forcing the signs here caused the UI and internal state to diverge when
  // opening a reading from Biblioteca Cósmica.

  const [savedCandidatesRefreshTick, setSavedCandidatesRefreshTick] = useState(0);
  const [remoteSavedQuickCandidates, setRemoteSavedQuickCandidates] = useState([]);

  useEffect(() => {
    if (refreshCompareStateKey) {
      setSavedCandidatesRefreshTick((prev) => prev + 1);
    }
  }, [refreshCompareStateKey]);

  useEffect(() => {
    let cancelled = false;

    async function refreshRemoteQuickCandidates() {
      const remote = await readRemoteQuickSavedSynastries();
      if (!cancelled) {
        setRemoteSavedQuickCandidates(remote);
      }
    }

    refreshRemoteQuickCandidates();
    return () => {
      cancelled = true;
    };
  }, [savedCandidatesRefreshTick, refreshCompareStateKey]);

  const displayedResult = useMemo(() => {
    if (result) return result;

    if (activeInitialQuickResult?.pair) {
      const savedA = normalizeQuickSignKey(activeInitialQuickResult.pair.signA);
      const savedB = normalizeQuickSignKey(activeInitialQuickResult.pair.signB);
      const currentA = normalizeQuickSignKey(signA);
      const currentB = normalizeQuickSignKey(signB);

      if (savedA === currentA && savedB === currentB) {
        return activeInitialQuickResult;
      }
    }

    return null;
  }, [result, activeInitialQuickResult, signA, signB]);

  const routeSavedCandidates = useMemo(() => {
    const rawCandidates = Array.isArray(location?.state?.savedCandidates)
      ? location.state.savedCandidates
      : [];

    return rawCandidates.filter((item) => inferSavedQuickMode(item) === 'quick');
  }, [location?.state?.savedCandidates]);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  const [savingQuickReading, setSavingQuickReading] = useState(false);
  const [libHover, setLibHover] = useState(false);
  const refreshSavedComparisonState = useCallback(() => {
  setSavedCandidatesRefreshTick((prev) => prev + 1);
  try {
    window.dispatchEvent(new Event('storage'));
  } catch {
    // ignore
  }
}, []);

  useEffect(() => {
    let rafId = 0;

    function handleResize() {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        setIsMobile(window.innerWidth <= 768);
      });
    }

    window.addEventListener('resize', handleResize);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(QUICK_STARS_STYLE_ID)) return undefined;

    const style = document.createElement('style');
    style.id = QUICK_STARS_STYLE_ID;
    style.innerHTML = `
    @keyframes quickStars {
      0% { transform: translateY(0px); opacity:0.25 }
      50% { transform: translateY(-8px); opacity:0.45 }
      100% { transform: translateY(0px); opacity:0.25 }
    }
    `;
    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  const handleSaveQuickReading = useCallback(async (quickResult) => {
  setSavingQuickReading(true);
  try {
    const payload = buildQuickSavedPayload(quickResult);
    const saved = await saveQuickSynastry(payload);
    setRemoteSavedQuickCandidates((prev) => mergeQuickCandidateLists(saved ? [saved] : [], prev));
    refreshSavedComparisonState();
    return saved;
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const errorCode = String(
      error?.code || error?.errorCode || error?.response?.data?.code || error?.response?.data?.errorCode || ''
    ).toLowerCase();

    const status = Number(error?.response?.status || 0);
    const backendMessage = String(
      error?.response?.data?.error || error?.response?.data?.message || error?.message || ''
    ).trim();

    if (status === 409 || backendMessage.toLowerCase().includes('conflict')) {
      setRemoteSavedQuickCandidates((prev) => mergeQuickCandidateLists([buildQuickSavedPayload(quickResult)], prev));
      refreshSavedComparisonState();
      return {
        ok: true,
        duplicate: true,
        message: synText('Esta lectura ya estaba guardada en tu Biblioteca Cósmica.', 'This reading was already saved in your Cosmic Library.'),
      };
    }

    if (
      errorCode === 'maxsavedsynastries' ||
      errorCode === 'library_limit_reached' ||
      errorCode === 'saved_synastry_limit' ||
      message.includes('library limit reached') ||
      message.includes('biblioteca cósmica ya tiene') ||
      message.includes('eliminá una para poder guardar una nueva sinastría') ||
      message.includes('maxsavedsynastries') ||
      message.includes('saved synastry limit')
    ) {
      throw new Error(
        synText('Tu Biblioteca Cósmica está llena. Podés guardar hasta 3 lecturas. Eliminá una lectura guardada para poder sumar una nueva.', 'Your Cosmic Library is full. You can save up to 3 readings. Delete a saved reading before adding a new one.')
      );
    }

    throw error;
  } finally {
    setSavingQuickReading(false);
  }
}, [refreshSavedComparisonState]);

  const handleOpenQuickComparison = useCallback((quickResult) => {
    const reading = quickResult || displayedResult;

    if (!reading) return;

    const translatedSignA = translateSign(reading?.pair?.signA || '');
    const translatedSignB = translateSign(reading?.pair?.signB || '');

    const fallbackSavedItems = readSavedSynastriesFromStorage();
    const sourceCandidates = mergeQuickCandidateLists(
      routeSavedCandidates,
      remoteSavedQuickCandidates,
      fallbackSavedItems
    );

    const quickCandidates = sourceCandidates.filter((item) => {
      if (inferSavedQuickMode(item) !== 'quick') return false;

      const payload = getSavedQuickResultPayload(item) || item;
      const savedSignA = normalizeQuickSignKey(
        payload?.pair?.signA || item?.personA?.sign || item?.personA?.sunSign || ''
      );
      const savedSignB = normalizeQuickSignKey(
        payload?.pair?.signB || item?.personB?.sign || item?.personB?.sunSign || ''
      );

      if (!savedSignA || !savedSignB) return false;

      const itemFingerprint = String(item?.fingerprint || item?.raw?.fingerprint || '').trim();
      const readingFingerprint = String(reading?.fingerprint || '').trim();
      const readingId = String(reading?.id || reading?._id || '').trim();
      const itemId = String(item?.id || item?._id || '').trim();

      const readingSignA = normalizeQuickSignKey(reading?.pair?.signA || '');
      const readingSignB = normalizeQuickSignKey(reading?.pair?.signB || '');

      // prevent same saved item
      if (readingId && itemId && itemId === readingId) return false;

      // prevent same fingerprint
      if (readingFingerprint && itemFingerprint && itemFingerprint === readingFingerprint) return false;

      // prevent same zodiac pair (A-B or B-A)
      const sameOrder = savedSignA === readingSignA && savedSignB === readingSignB;
      const reversedOrder = savedSignA === readingSignB && savedSignB === readingSignA;

      if (sameOrder || reversedOrder) return false;

      return true;
    });

    if (!quickCandidates.length) {
      throw new Error(
        synText('Todavía no tenés otra lectura solar guardada para comparar. Guardá al menos una compatibilidad solar más en tu Biblioteca Cósmica y después volvé a intentar.', 'You do not have another saved solar reading to compare yet. Save at least one more solar compatibility reading in your Cosmic Library and try again.')
      );
    }

    navigate('/synastry-comparison', {
      state: {
        from: 'quick',
        currentReading: reading,
        currentPeople: {
          personA: {
            nickname: translatedSignA || synText('Persona A', 'Person A'),
            sign: translatedSignA || reading?.pair?.signA || '',
            sunSign: translatedSignA || reading?.pair?.signA || '',
          },
          personB: {
            nickname: translatedSignB || synText('Persona B', 'Person B'),
            sign: translatedSignB || reading?.pair?.signB || '',
            sunSign: translatedSignB || reading?.pair?.signB || '',
          },
        },
        readingMode: 'quick',
        savedCandidates: quickCandidates,
      },
    });
  }, [displayedResult, navigate, routeSavedCandidates, remoteSavedQuickCandidates]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.cosmicBg} aria-hidden />
      <div style={styles.topActions}>
        <button
          type="button"
          onMouseEnter={() => setLibHover(true)}
          onMouseLeave={() => setLibHover(false)}
          onClick={() =>
            navigate('/saved-synastries', {
              state: { from: 'quick' },
            })
          }
          style={{
            ...styles.libraryButton,
            ...(libHover ? styles.libraryButtonHover : {}),
          }}
        >
          {synText('✦ Biblioteca Cósmica', '✦ Cosmic Library')}
        </button>
      </div>
      <div style={styles.sectionTitle}>{synText('Compatibilidad Zodiacal', 'Zodiac Compatibility')}</div>
      <div style={styles.introCard}>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 'auto -70px -80px auto',
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(247,215,116,0.16) 0%, rgba(247,215,116,0) 72%)',
            filter: 'blur(14px)',
            pointerEvents: 'none',
          }}
        />
        <div style={styles.introTitle}>{synText('Rápido', 'Quick')}</div>
        <div style={{ fontWeight: 700, color: '#FFE7A3', marginBottom: 4 }}>Solar</div>
        {synText('Entrá por Solar si buscás una primera lectura por signos solares, o usá Modo Profundo para desbloquear una experiencia más seria con fecha, hora, lugar y capas interpretativas mucho más completas.', 'Choose Solar for an initial sun-sign reading, or use Deep Mode for a more detailed experience with date, time, place, and richer interpretation layers.')}
      </div>

      <QuickCompatibilityForm
        signA={signA}
        signB={signB}
        onChangeSignA={setSignA}
        onChangeSignB={setSignB}
        onSubmit={calculate}
        loading={loading}
        isMobile={isMobile}
      />

      {error && (
        <div style={styles.error}>{error.message || String(error)}</div>
      )}

      <div style={styles.resultSection}>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(247,215,116,0.14) 0%, rgba(247,215,116,0) 72%)',
            filter: 'blur(14px)',
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: -60,
            bottom: -60,
            width: 240,
            height: 240,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(138,43,226,0.16) 0%, rgba(138,43,226,0) 72%)',
            filter: 'blur(16px)',
            pointerEvents: 'none',
          }}
        />
        <QuickResultCard
          result={displayedResult}
          isMobile={isMobile}
          onSaveQuickReading={handleSaveQuickReading}
          onCompareQuickReading={handleOpenQuickComparison}
          savingQuickReading={savingQuickReading}
        />
      </div>
    </div>
  );
}
